import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { ModalContainerComponent } from '../../../shared/components/modal-container/modal-container.component';
import { IBreadcrumbs } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { catchError, firstValueFrom, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { UserService } from '../../../core/services/user.service';
import { FacilitiesService } from '../../../core/services/facilities.service';
import { SensorService } from '../../../core/services/sensors.service';
import { AuthService } from '../../../core/services/auth.service';
import { CustomerService } from '../../../core/services/customer.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { RequestsService } from '../../../core/services/utils/requests.service';
import { addIcons } from 'ionicons';
import {
  alertCircle,
  analyticsOutline,
  checkmarkCircle,
  chevronDownOutline,
  ellipseOutline,
  hardwareChipOutline,
  pulseOutline,
  refreshOutline,
  serverOutline,
  speedometerOutline,
  thermometerOutline,
  warningOutline
} from 'ionicons/icons';
import {
  DeviceDiagnosticsFilters,
  DeviceDiagnosticsResponse,
  DiagnosticAlert,
  DiagnosticCheck,
  DiagnosticSeverity
} from '../../../shared/models/be/deviceDiagnostics';
import { Router } from '@angular/router';
import { TableService } from '../../../shared/components/table/table.service';
import moment from 'moment';
import {
  buildSensorConnectivityTargets,
  DiagnosticScope,
  SensorConnectivityTarget
} from './diagnostic-scope.utils';
import { StereoCalibrationHealthState } from '../../../shared/models/be/device';

interface DiagnosticNavigationParams {
  target: string;
  filters: Array<{ id: string; value?: string }>;
}

interface DiagnosticIssue {
  message: string;
  severity: 'warning' | 'error';
  redirectTo?: string;
  params?: DiagnosticNavigationParams;
}

interface DiagnosticStep {
  id: string;
  label: string;
  state: 'pending' | 'running' | 'success' | 'error' | 'warning';
  details?: string;
  issues: DiagnosticIssue[];
  expanded: boolean;
}

interface DiagnosticCheckStep {
  check: DiagnosticCheck;
  state: 'pending' | 'running' | 'success' | 'error' | 'warning';
  details: string;
  expanded: boolean;
}


type DiagnosticsLoadState = 'pending' | 'running' | 'success' | 'error';

const CHECK_PRESENTATION: Record<string, { icon: string; label: string }> = {
  temperature: { icon: 'thermometer-outline', label: 'Temperatura' },
  cpu_usage: { icon: 'speedometer-outline', label: 'Utilizzo CPU' },
  memory_usage: { icon: 'hardware-chip-outline', label: 'Utilizzo RAM' },
  disk_usage: { icon: 'server-outline', label: 'Utilizzo disco' },
  stereo_calibration_health: { icon: 'analytics-outline', label: 'Calibrazione stereo' }
};

@Component({
  selector: 'app-diagnostic',
  templateUrl: './diagnostic.component.html',
  styleUrls: ['./diagnostic.component.scss'],
  imports: [IonicModule, ModalContainerComponent]
})
export class DiagnosticComponent {
  status: 'idle' | 'running' | 'finished' = 'idle';
  diagnosticsState: DiagnosticsLoadState = 'pending';
  diagnosticsError = '';
  deviceDiagnostics: DeviceDiagnosticsResponse | null = null;
  diagnosticChecks: DiagnosticCheckStep[] = [];

  readonly diagnosticScope: DiagnosticScope;
  diagnosticSteps: DiagnosticStep[] = [
    { id: 'services', label: 'Stato servizi', state: 'pending', issues: [], expanded: true },
    { id: 'sensors_connectivity', label: 'Stato connessione sensori', state: 'pending', issues: [], expanded: true }
  ];

  public breadcrumbsList: IBreadcrumbs[] = [{
    icon: 'monitor_heart',
    routerLink: '',
    label: 'DIAGNOSTIC'
  }];

  constructor(
    private userService: UserService,
    private facilitiesService: FacilitiesService,
    private sensorService: SensorService,
    private authService: AuthService,
    private customerService: CustomerService,
    private analyticsService: AnalyticsService,
    private requestsService: RequestsService,
    private router: Router,
    private tableService: TableService
  ) {
    this.diagnosticScope = this.resolveDiagnosticScope();

    addIcons({
      'ellipse-outline': ellipseOutline,
      'checkmark-circle': checkmarkCircle,
      'alert-circle': alertCircle,
      'chevron-down-outline': chevronDownOutline,
      'warning-outline': warningOutline,
      'pulse-outline': pulseOutline,
      'thermometer-outline': thermometerOutline,
      'speedometer-outline': speedometerOutline,
      'hardware-chip-outline': hardwareChipOutline,
      'server-outline': serverOutline,
      'analytics-outline': analyticsOutline,
      'refresh-outline': refreshOutline
    });
  }

  async startDiagnostic(): Promise<void> {
    if (this.status === 'running') {
      return;
    }

    this.status = 'running';
    this.diagnosticsState = 'pending';
    this.diagnosticsError = '';
    this.deviceDiagnostics = null;
    this.diagnosticChecks = [];
    this.diagnosticSteps.forEach(step => {
      step.state = 'pending';
      step.issues = [];
      step.details = '';
      step.expanded = true;
    });

    await this.checkServices(0);
    await this.checkSensorsConnection(1);
    await this.checkDeviceDiagnostics();

    this.status = 'finished';
  }

  private checkServices(index: number): Promise<unknown> {
    const step = this.diagnosticSteps[index];
    step.state = 'running';

    return firstValueFrom(
      forkJoin({
        'Servizio Utenti': this.userService.getServiceStatus$().pipe(catchError(() => of(false))),
        'Servizio Strutture': this.facilitiesService.getServiceStatus$().pipe(catchError(() => of(false))),
        'Servizio Sensori': this.sensorService.getServiceStatus$().pipe(catchError(() => of(false))),
        'Servizio Autenticazione': this.authService.getServiceStatus$().pipe(catchError(() => of(false))),
        'Servizio Clienti': this.customerService.getServiceStatus$().pipe(catchError(() => of(false))),
        'Servizio Analytics': this.analyticsService.getServiceStatus$().pipe(catchError(() => of(false)))
      }).pipe(
        tap(results => {
          Object.entries(results).forEach(([service, online]) => {
            if (!online) {
              step.issues.push({ message: `${service} Offline`, severity: 'error' });
            }
          });
          step.state = step.issues.length > 0 ? 'error' : 'success';
          step.details = step.state === 'success' ? 'Online' : '';
        }),
        catchError(() => {
          step.issues.push({ message: 'Impossibile verificare lo stato dei servizi', severity: 'error' });
          step.state = 'error';
          return of(null);
        })
      )
    );
  }

  private checkSensorsConnection(index: number): Promise<unknown> {
    const step = this.diagnosticSteps[index];
    step.state = 'running';

    const reportIssue = (message: string, severity: 'warning' | 'error', target?: SensorConnectivityTarget) => {
      step.issues.push({
        message,
        severity,
        redirectTo: target ? '/wita/devices' : undefined,
        params: target ? {
          target: 'devices',
          filters: target.filters
        } : undefined
      });
    };

    return firstValueFrom(
      this.getSensorConnectivityTargets$().pipe(
        switchMap(targets => targets.length === 0
          ? of([])
          : forkJoin(targets.map(target =>
            this.analyticsService.getKpiSensors$({
              customerId: target.customerId,
              organizationId: target.organizationId
            }).pipe(
              tap(result => {
                if (result.online === 0) {
                  reportIssue(`${target.label} - Nessun sensore online su ${result.total}`, 'error', target);
                } else if (result.online !== result.total) {
                  reportIssue(`${target.label} - ${result.total - result.online} sensori offline su ${result.total} presenti`, 'warning', target);
                }
              }),
              catchError(() => {
                reportIssue(`${target.label} - Errore chiamata API`, 'error', target);
                return of(null);
              })
            )
          ))
        ),
        tap(() => {
          if (step.issues.some(issue => issue.severity === 'error')) {
            step.state = 'error';
          } else if (step.issues.some(issue => issue.severity === 'warning')) {
            step.state = 'warning';
            step.details = 'Offline';
          } else {
            step.state = 'success';
            step.details = 'Online';
          }
        }),
        catchError(() => {
          reportIssue('Impossibile verificare la connessione dei sensori', 'error');
          step.state = 'error';
          return of(null);
        })
      )
    );
  }

  private getSensorConnectivityTargets$(): Observable<SensorConnectivityTarget[]> {
    if (this.diagnosticScope.type !== 'global') {
      return of(buildSensorConnectivityTargets(this.diagnosticScope, []));
    }

    return this.customerService.getCustomers$().pipe(
      map(customers => buildSensorConnectivityTargets(this.diagnosticScope, customers))
    );
  }

  private async checkDeviceDiagnostics(): Promise<void> {
    this.diagnosticsState = 'running';
    this.diagnosticsError = '';
    this.deviceDiagnostics = null;
    this.diagnosticChecks = [];

    const filters: DeviceDiagnosticsFilters = {
      customerId: this.diagnosticScope.customerId,
      organizationId: this.diagnosticScope.organizationId
    };

    const response = await firstValueFrom(
      this.sensorService.getDeviceDiagnostics$(filters).pipe(
        catchError(error => {
          this.diagnosticsError = this.getDiagnosticsErrorMessage(error);
          this.diagnosticsState = 'error';
          return of(null);
        })
      )
    );

    if (!response) {
      return;
    }

    this.deviceDiagnostics = response;
    this.diagnosticChecks = response.checks.map(check => ({
      check: {
        ...check,
        alerts: this.sortAlerts(check.alerts)
      },
      state: 'pending',
      details: '',
      expanded: true
    }));
    this.diagnosticsState = 'success';

    for (const step of this.diagnosticChecks) {
      step.state = 'running';
      await this.wait(350);
      step.state = step.check.status === 'ok' ? 'success' : step.check.status;
      step.details = this.getCheckDetails(step.check);
      await this.wait(150);
    }
  }

  async retryDeviceDiagnostics(): Promise<void> {
    if (this.diagnosticsState === 'running') {
      return;
    }

    this.status = 'running';
    await this.checkDeviceDiagnostics();
    this.status = 'finished';
  }

  toggleStep(step: DiagnosticStep): void {
    step.expanded = !step.expanded;
  }

  toggleMetric(metric: DiagnosticCheckStep): void {
    metric.expanded = !metric.expanded;
  }

  getCheckLabel(check: DiagnosticCheck): string {
    return CHECK_PRESENTATION[check.key]?.label || check.label || this.humanizeKey(check.key);
  }

  getCheckIcon(check: DiagnosticCheck): string {
    return CHECK_PRESENTATION[check.key]?.icon || 'analytics-outline';
  }

  getCheckTitle(check: DiagnosticCheck): string {
    const titles: Record<string, string> = {
      temperature: 'Stato temperatura sensori',
      cpu_usage: 'Stato utilizzo CPU sensori',
      memory_usage: 'Stato utilizzo RAM sensori',
      disk_usage: 'Stato utilizzo disco sensori',
      stereo_calibration_health: 'Stato calibrazione stereo sensori'
    };
    return titles[check.key] || `Stato ${this.getCheckLabel(check).toLocaleLowerCase('it-IT')} sensori`;
  }

  getAlertMessage(check: DiagnosticCheck, alert: DiagnosticAlert): string {
    const device = this.getDeviceName(alert);
    const macAddress = alert.device.macAddress && alert.device.macAddress !== device
      ? ` (${alert.device.macAddress})`
      : '';

    if (check.key === 'stereo_calibration_health') {
      const health = alert.device.stereoCalibrationHealth;
      const state = health?.state;
      const stateLabel = state ? this.getStereoCalibrationStateLabel(state) : 'non noto';
      const rawState = state ? ` (${state})` : '';
      const reason = health?.reason ? ` - ${health.reason}` : '';
      return `${device}${macAddress} - Calibrazione stereo: stato ${stateLabel}${rawState}${reason}`;
    }

    if (alert.reason === 'data_unavailable') {
      return `${device}${macAddress} - Dati telemetrici non disponibili`;
    }

    const severityLabel = alert.severity === 'error' ? 'critico' : 'anomalo';
    const threshold = this.getThresholdDescription(check, alert.severity);
    return `${device}${macAddress} - ${this.getCheckLabel(check)}: valore ${severityLabel} rilevato! - ${this.formatValue(alert.value, check.unit)}${threshold ? ` (${threshold})` : ''}`;
  }

  getThresholdDescription(check: DiagnosticCheck, severity: DiagnosticSeverity): string {
    const threshold = check.thresholds.find(item => item.severity === severity);
    if (!threshold) {
      return '';
    }

    const operators: Record<string, string> = { gt: '>', gte: '>=', lt: '<', lte: '<=' };
    return `${severity === 'error' ? 'Soglia critica' : 'Soglia warning'} ${operators[threshold.operator]} ${this.formatValue(threshold.value, check.unit)}`;
  }

  formatValue(value: number | null, unit: string): string {
    if (value === null) {
      return '\u2014';
    }

    const formatted = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(value);
    if (unit === 'celsius') {
      return `${formatted} \u00B0C`;
    }
    if (unit === 'percent') {
      return `${formatted}%`;
    }
    return unit ? `${formatted} ${unit}` : formatted;
  }

  getDeviceName(alert: DiagnosticAlert): string {
    return alert.device.label || alert.device.macAddress || alert.device.id;
  }

  getDeviceLocation(alert: DiagnosticAlert): string {
    const facilities = alert.device.facilities;
    if (!facilities) {
      return '';
    }

    return ['customer', 'organization', 'building', 'unit', 'room']
      .map(key => facilities[key])
      .map(value => {
        if (!value || typeof value !== 'object') {
          return '';
        }
        const facility = value as Record<string, unknown>;
        return String(facility['name'] || facility['businessName'] || facility['label'] || '');
      })
      .filter(Boolean)
      .join(' > ');
  }

  formatHeartbeat(value: string | null): string {
    return value ? moment(value).format('DD/MM/YYYY HH:mm') : 'Non disponibile';
  }

  reset(): void {
    this.status = 'idle';
    this.diagnosticsState = 'pending';
    this.diagnosticsError = '';
    this.deviceDiagnostics = null;
    this.diagnosticChecks = [];
  }

  navigateDetails(redirectTo: string, params?: DiagnosticNavigationParams): void {
    if (!params) {
      return;
    }
    this.tableService.updateTablePreferences(params.target, 'filters', params.filters);
    this.router.navigate([redirectTo]);
  }

  private sortAlerts(alerts: DiagnosticAlert[]): DiagnosticAlert[] {
    const priority: Record<DiagnosticSeverity, number> = { error: 0, warning: 1 };
    return [...alerts].sort((first, second) => priority[first.severity] - priority[second.severity]);
  }

  private humanizeKey(key: string): string {
    const value = key.replace(/_/g, ' ');
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private getStereoCalibrationStateLabel(state: StereoCalibrationHealthState): string {
    const labels: Record<StereoCalibrationHealthState, string> = {
      learning: 'in apprendimento',
      stable: 'stabile',
      drifting: 'in deriva',
      degraded: 'degradata',
      unobservable: 'non osservabile',
      monitor_error: 'errore di monitoraggio'
    };
    return labels[state];
  }

  private getCheckDetails(check: DiagnosticCheck): string {
    if (check.status === 'ok') {
      return `${check.summary.evaluated} dispositivi verificati`;
    }
    if (check.status === 'warning') {
      return check.summary.unavailable > 0 && check.summary.warning === 0
        ? 'Dati non disponibili'
        : 'Verificare!';
    }
    return 'Rilevati problemi';
  }

  private wait(milliseconds: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
  }

  private resolveDiagnosticScope(): DiagnosticScope {
    const currentUser = this.requestsService.getCurrentUser();
    const customerId = currentUser?.customer?.id ?? (currentUser?.customerId || undefined);
    const organizationId = currentUser?.organization?.id ?? (currentUser?.organizationId || undefined);

    if (organizationId) {
      return {
        type: 'organization',
        label: `Organizzazione: ${currentUser?.organization?.businessName || organizationId}`,
        description: 'La scansione riguarda i sensori dell\'organizzazione associata al tuo profilo.',
        customerId,
        organizationId
      };
    }

    if (customerId !== undefined) {
      return {
        type: 'customer',
        label: `Cliente: ${currentUser?.customer?.businessName || customerId}`,
        description: 'La scansione riguarda i sensori del cliente associato al tuo profilo.',
        customerId
      };
    }

    return {
      type: 'global',
      label: 'GLOBALE',
      description: 'La scansione riguarda tutti i sensori accessibili al tuo profilo.',
    };
  }


  private getDiagnosticsErrorMessage(error: unknown): string {
    if (!(error instanceof HttpErrorResponse)) {
      return 'Errore imprevisto durante il caricamento della diagnostica.';
    }

    if (error.status === 0) {
      return 'Impossibile raggiungere il servizio di diagnostica.';
    }
    if (error.status === 401) {
      return 'Sessione scaduta. Effettua nuovamente l\'accesso.';
    }
    if (error.status === 403) {
      return 'Non hai i permessi per visualizzare la diagnostica.';
    }
    if (error.status === 404) {
      return "Il servizio di diagnostica non e' disponibile.";
    }
    if (error.status >= 500) {
      return "Il servizio di diagnostica e' temporaneamente non disponibile.";
    }
    return `Impossibile caricare la diagnostica (errore ${error.status}).`;
  }
}
